import { colorSchemeOptionListAtom } from 'features/generator/contentFormSections/client/ContentDesignForm'
import { atom } from 'jotai'

import {
	primaryColorAtom,
	secondaryColorAtom,
	selectedColorSchemeAtom,
} from '@/content-from-sections/client'
import { TUploadedMedia } from '@/services'
import { getFileFromMedia, isOnServer } from '@/utils'

import {
	facebookButtonListAtom,
	facebookNameAtom,
	facebookTitleAtom,
	facebookUrlAtom,
	facebookWebsiteAtom,
	imageFileListAtom,
	imageListAtom,
	TQrColorScheme,
	welcomeImageAtom,
	welcomeImageFileAtom,
} from '../../contentFormSections'
import { QR_TYPE } from '../../generatorTypes'

export type TFacebookContent = {
	type: QR_TYPE.Facebook
	colors: Omit<TQrColorScheme, 'name'>
	gallery: TUploadedMedia[]
	info: { url: string; name: string; title: string; website: string }
	buttons: { title: string; url: string | undefined }[]
	loaderImage: TUploadedMedia
}

export const facebookContentAtom = atom<
	TFacebookContent,
	[TFacebookContent],
	void
>(
	(get) => ({
		type: QR_TYPE.Facebook as const,
		colors: {
			primary: get(primaryColorAtom),
			secondary: get(secondaryColorAtom),
		},
		gallery: get(imageListAtom),
		buttons: get(facebookButtonListAtom),
		info: {
			url: get(facebookUrlAtom) || '',
			name: get(facebookNameAtom) || '',
			title: get(facebookTitleAtom) || '',
			website: get(facebookWebsiteAtom) || '',
		},
		loaderImage:
			get(welcomeImageAtom) ||
			({
				publicId: '',
				resourceType: '',
				format: '',
				bytes: 0,
			} as TUploadedMedia),
	}),
	(get, set, content: TFacebookContent) => {
		// Color scheme
		const colorScheme = get(colorSchemeOptionListAtom).find(
			(scheme) =>
				scheme.primary === content.colors.primary &&
				scheme.secondary === content.colors.secondary,
		)

		set(selectedColorSchemeAtom, colorScheme?.name ?? '')
		set(primaryColorAtom, content.colors.primary)
		set(secondaryColorAtom, content.colors.secondary)

		set(imageListAtom, content.gallery)
		if (!isOnServer) {
			setTimeout(async () => {
				try {
					const imageFileList = await Promise.all(
						content.gallery.map(async (image) => await getFileFromMedia(image)),
					)
					set(imageFileListAtom, imageFileList)
				} catch (error) {
					console.error(error)
				}
			}, 0)
		}
		set(facebookButtonListAtom, content.buttons)
		set(facebookUrlAtom, content.info.url)
		set(facebookNameAtom, content.info.name)
		set(facebookTitleAtom, content.info.title)
		set(facebookWebsiteAtom, content.info.website)

		// Welcome screen
		set(welcomeImageAtom, content.loaderImage)
		if (!isOnServer) {
			setTimeout(async () => {
				try {
					const loaderFile = await getFileFromMedia(content.loaderImage)
					set(welcomeImageFileAtom, loaderFile)
				} catch (error) {
					console.error(error)
				}
			}, 0)
		}
	},
)
